import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { AlertCircle, ArrowRight, Scale, ShieldCheck } from "lucide-react";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { Spinner } from "@client/components/ui/spinner";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@local");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/sign-in", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        if (res.status === 401) setError("Email ou senha incorretos.");
        else if (res.status === 423) {
          const body = await res.json().catch(() => null);
          const lockedUntil = body?.lockedUntil
            ? new Date(body.lockedUntil).toLocaleTimeString("pt-BR")
            : "alguns minutos";
          setError(`Conta bloqueada até ${lockedUntil}. Tente novamente mais tarde.`);
        } else if (res.status === 400) {
          setError("Dados inválidos. Confira email e senha.");
        } else {
          setError(`Erro inesperado (${res.status}).`);
        }
        return;
      }
      await navigate({ to: "/dashboard" });
    } catch {
      setError("Não foi possível conectar ao servidor.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <aside className="hidden lg:flex relative flex-col justify-between p-12 hairline-r overflow-hidden bg-[var(--color-surface-2)]">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 15%, rgba(122,31,31,0.045), transparent 55%), radial-gradient(circle at 90% 95%, rgba(140,110,60,0.04), transparent 50%)`,
          }}
        />
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)] to-transparent opacity-60"
        />
        <div
          aria-hidden
          className="absolute left-12 top-1/2 -translate-y-1/2 w-px h-32 bg-gradient-to-b from-transparent via-[var(--color-gold-dim)] to-transparent opacity-50"
        />

        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-[var(--color-ink)] text-[var(--color-surface)] shadow-[var(--shadow-soft)]">
              <Scale className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <span className="text-display text-base font-semibold tracking-[-0.01em] text-[var(--color-ink)]">
              Gestão<span className="text-[var(--color-gold)]">·</span>Advogado
            </span>
          </div>
        </div>

        <div className="relative max-w-xl pl-6">
          <span
            aria-hidden
            className="absolute left-0 top-2 h-[calc(100%-1rem)] w-[3px] bg-[var(--color-gold)]"
          />
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-gold)]">
            Para o seu escritório
          </p>
          <h1 className="text-display mt-4 text-[46px] leading-[1.02] font-medium tracking-[-0.025em] text-[var(--color-ink)]">
            Toda peça processual,
            <br />
            <span className="italic text-[var(--color-gold)]">contrato</span> e
            <br />
            parecer do escritório
            <br />
            em um único lugar.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-[var(--color-ink-muted)] max-w-md">
            Centralize as peças, contratos e pareceres do escritório em um espaço privado, acessível
            apenas por quem você autoriza.
          </p>

          <ul className="mt-10 space-y-3 max-w-md">
            {[
              "Organize documentos em pastas por cliente ou processo",
              "Encontre rapidamente por nome ou tag",
              "Cada acesso fica registrado para auditoria",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-sm text-[var(--color-ink-2)]">
                <span
                  aria-hidden
                  className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--color-gold)] shrink-0"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex items-center gap-2.5 text-sm text-[var(--color-ink-muted)]">
          <ShieldCheck className="h-4 w-4 text-[var(--color-gold)]" aria-hidden />
          <span>Conexão segura · Dados criptografados · Histórico auditável</span>
        </div>
      </aside>

      <main className="flex items-center justify-center p-6 lg:p-12 bg-[var(--color-canvas)]">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-10 flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-[var(--color-ink)] text-[var(--color-surface)]">
              <Scale className="h-4 w-4" strokeWidth={2.2} />
            </span>
            <span className="text-display text-base font-semibold tracking-[-0.01em] text-[var(--color-ink)]">
              Gestão<span className="text-[var(--color-gold)]">·</span>Advogado
            </span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--color-gold)]">
            Acesso restrito
          </p>
          <h2 className="text-display mt-2 text-3xl font-semibold tracking-tight">
            Entrar na plataforma
          </h2>
          <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
            Use as credenciais fornecidas pelo administrador do escritório.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="voce@escritorio.com.br"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••••"
                required
              />
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2.5 rounded-md border border-[color:color-mix(in_srgb,var(--color-danger)_40%,transparent)] bg-[var(--color-danger-wash)] px-3 py-2.5 text-sm text-[var(--color-danger)]"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" size="lg" className="w-full mt-2" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" />
                  Verificando…
                </>
              ) : (
                <>
                  Entrar
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>

            <p className="text-xs leading-relaxed text-[var(--color-ink-muted)] pt-2">
              Após 5 tentativas incorretas, o acesso é bloqueado temporariamente por segurança.
              <br />
              Esqueceu a senha? Fale com o administrador do escritório.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
